/**
 * Futures (COIN-M) WebSocket API
 *
 * Access market data, manage accounts, and trade COIN-M perpetual and delivery futures.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { WebsocketAPIBase, WebsocketApiResponse, WebsocketSendMsgOptions } from '@binance/common';
import type {
    CancelOrderResponse,
    ModifyOrderResponse,
    NewOrderResponse,
    PositionInformationResponse,
    QueryOrderResponse,
} from '../types';

/**
 * TradeApi - interface
 *
 * @interface TradeApi
 */
export interface TradeApiInterface {
    /**
     * Cancel an active order.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     * - Either `orderId` or `origClientOrderId` must be sent.
     *
     * @summary Cancel Order (TRADE)
     * @param {CancelOrderRequest} requestParameters Request parameters.
     *
     * @returns {Promise<CancelOrderResponse>}
     * @memberof TradeApiInterface
     */
    cancelOrder(
        requestParameters: CancelOrderRequest
    ): Promise<WebsocketApiResponse<CancelOrderResponse>>;

    /**
     * Order modify function, currently only LIMIT order modification is
     * supported, modified orders will be reordered in the match queue
     *
     * Weight: 1 on 10s order rate limit(X-MBX-ORDER-COUNT-10S);
     * 1 on 1min order rate limit(X-MBX-ORDER-COUNT-1M);
     * 1 on IP rate limit(x-mbx-used-weight-1m)
     *
     * Security Type: TRADE
     *
     * Notes:
     * - Either `orderId` or `origClientOrderId` must be sent, and the `orderId` will prevail if both are sent.
     * - Both `quantity` and `price` must be sent.
     * - When the new `quantity` or `price` doesn't satisfy `PRICE_FILTER` / `PERCENT_FILTER` / `LOT_SIZE`, amendment will be rejected and the order will stay as it is.
     * - However the order will be cancelled by the amendment in the following situations:
     * - when the order is in partially filled status and the new `quantity` <= `executedQty`
     * - When the order is `GTX` and the new price will cause it to be executed immediately
     * - One order can only be modified for less than 10000 times.
     *
     * @summary Modify Order (TRADE)
     * @param {ModifyOrderRequest} requestParameters Request parameters.
     *
     * @returns {Promise<ModifyOrderResponse>}
     * @memberof TradeApiInterface
     */
    modifyOrder(
        requestParameters: ModifyOrderRequest
    ): Promise<WebsocketApiResponse<ModifyOrderResponse>>;

    /**
     * Send in a new order.
     *
     * Weight(IP): 0
     *
     * Security Type: TRADE
     *
     * Notes:
     * - Additional mandatory parameters based on `type`:
     * | Type | Additional mandatory parameters |  | :---: | --- |  | `LIMIT` | `timeInForce`, `quantity`, `price` | | `MARKET` | `quantity` | | `STOP/TAKE_PROFIT` | `quantity`, `price`, `stopPrice` | | `STOP_MARKET/TAKE_PROFIT_MARKET` | `stopPrice` | | `TRAILING_STOP_MARKET` | `callbackRate` |
     * - Order with type `STOP`, parameter `timeInForce` can be sent ( default `GTC`). * Order with type `TAKE_PROFIT`, parameter `timeInForce` can be sent ( default `GTC`). * Condition orders will be triggered when:
     * - If parameter `priceProtect` is sent as true:
     * - when price reaches the `stopPrice`，the difference rate between "MARK_PRICE" and "CONTRACT_PRICE" cannot be larger than the "triggerProtect" of the symbol
     * - "triggerProtect" of a symbol can be got from `GET /dapi/v1/exchangeInfo`
     * - `STOP`, `STOP_MARKET`:
     * - BUY: latest price ("MARK_PRICE" or "CONTRACT_PRICE") >= `stopPrice`
     * - SELL: latest price ("MARK_PRICE" or "CONTRACT_PRICE") <= `stopPrice`
     * - `TAKE_PROFIT`, `TAKE_PROFIT_MARKET`:
     * - BUY: latest price ("MARK_PRICE" or "CONTRACT_PRICE") <= `stopPrice`
     * - SELL: latest price ("MARK_PRICE" or "CONTRACT_PRICE") >= `stopPrice`
     * - `TRAILING_STOP_MARKET`:
     * - BUY: the lowest price after order placed <= `activationPrice`, and the latest price >= the lowest price * (1 + `callbackRate`)
     * - SELL: the highest price after order placed >= `activationPrice`, and the latest price <= the highest price * (1 - `callbackRate`)
     * - For `TRAILING_STOP_MARKET`, if you got such error code.
     * - BUY: `activationPrice` should be smaller than latest price.
     * - SELL: `activationPrice` should be larger than latest price.
     * - If `newOrderRespType` is sent as `RESULT`:
     * - `MARKET` order: the final FILLED result of the order will be return directly.
     * - `LIMIT` order with special `timeInForce`: the final status result of the order(FILLED or EXPIRED) will be returned directly.
     * - `STOP_MARKET`, `TAKE_PROFIT_MARKET` with `closePosition=true`:
     * - Follow the same rules for condition orders.
     * - If triggered，**close all** current long position(if `SELL`) or current short position(if `BUY`).
     * - Cannot be used with `quantity` parameter
     * - Cannot be used with `reduceOnly` parameter
     * - In Hedge Mode, cannot be used with `BUY` orders in `LONG` position side. and cannot be used with `SELL` orders in `SHORT` position side
     *
     * @summary New Order (TRADE)
     * @param {NewOrderRequest} requestParameters Request parameters.
     *
     * @returns {Promise<NewOrderResponse>}
     * @memberof TradeApiInterface
     */
    newOrder(requestParameters: NewOrderRequest): Promise<WebsocketApiResponse<NewOrderResponse>>;

    /**
     * Get current position information.
     *
     * Weight(IP): 5
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Please use with user data stream `ACCOUNT_UPDATE` to meet your timeliness and accuracy needs.
     *
     * @summary Position Information (USER_DATA)
     * @param {PositionInformationRequest} requestParameters Request parameters.
     *
     * @returns {Promise<PositionInformationResponse>}
     * @memberof TradeApiInterface
     */
    positionInformation(
        requestParameters?: PositionInformationRequest
    ): Promise<WebsocketApiResponse<PositionInformationResponse>>;

    /**
     * Check an order's status.
     *
     * These orders will not be found:
     * order status is `CANCELED` or `EXPIRED` **AND** order has NO filled trade **AND** created time + 3 days < current time
     * order create time + 90 days < current time
     *
     * Weight(IP): 1
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Either `orderId` or `origClientOrderId` must be sent.
     *
     * @summary Query Order (USER_DATA)
     * @param {QueryOrderRequest} requestParameters Request parameters.
     *
     * @returns {Promise<QueryOrderResponse>}
     * @memberof TradeApiInterface
     */
    queryOrder(
        requestParameters: QueryOrderRequest
    ): Promise<WebsocketApiResponse<QueryOrderResponse>>;
}

/**
 * Request parameters for cancelOrder operation in TradeApi.
 * @interface CancelOrderRequest
 */
export interface CancelOrderRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiCancelOrder
     */
    readonly symbol: string;

    /**
     *
     * @type {string}
     * @memberof TradeApiCancelOrder
     */
    readonly id?: string;

    /**
     *
     * @type {number | bigint}
     * @memberof TradeApiCancelOrder
     */
    readonly orderId?: number | bigint;

    /**
     *
     * @type {string}
     * @memberof TradeApiCancelOrder
     */
    readonly origClientOrderId?: string;

    /**
     *
     * @type {number | bigint}
     * @memberof TradeApiCancelOrder
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for modifyOrder operation in TradeApi.
 * @interface ModifyOrderRequest
 */
export interface ModifyOrderRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiModifyOrder
     */
    readonly symbol: string;

    /**
     *
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiModifyOrder
     */
    readonly side: ModifyOrderSideEnum;

    /**
     * Order quantity, cannot be sent with `closePosition=true`
     * @type {number}
     * @memberof TradeApiModifyOrder
     */
    readonly quantity: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiModifyOrder
     */
    readonly price: number;

    /**
     *
     * @type {string}
     * @memberof TradeApiModifyOrder
     */
    readonly id?: string;

    /**
     *
     * @type {number | bigint}
     * @memberof TradeApiModifyOrder
     */
    readonly orderId?: number | bigint;

    /**
     *
     * @type {string}
     * @memberof TradeApiModifyOrder
     */
    readonly origClientOrderId?: string;

    /**
     * only avaliable for `LIMIT`/`STOP`/`TAKE_PROFIT` order; Can't be passed together with `price`
     * @type {'OPPONENT' | 'OPPONENT_5' | 'OPPONENT_10' | 'OPPONENT_20' | 'QUEUE' | 'QUEUE_5' | 'QUEUE_10' | 'QUEUE_20'}
     * @memberof TradeApiModifyOrder
     */
    readonly priceMatch?: ModifyOrderPriceMatchEnum;

    /**
     * User-defined modification identifier, returned as-is in the response. Optional; not validated for uniqueness.
     * @type {number | bigint}
     * @memberof TradeApiModifyOrder
     */
    readonly modifyId?: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof TradeApiModifyOrder
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for newOrder operation in TradeApi.
 * @interface NewOrderRequest
 */
export interface NewOrderRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiNewOrder
     */
    readonly symbol: string;

    /**
     *
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiNewOrder
     */
    readonly side: NewOrderSideEnum;

    /**
     * **After CM migration, stop-type values (`STOP`, `STOP_MARKET`, `TAKE_PROFIT`, `TAKE_PROFIT_MARKET`, `TRAILING_STOP_MARKET`) are no longer accepted and will return `-4120`. Use the REST `/dapi/v1/algoOrder` endpoint instead.**
     * @type {'LIMIT' | 'MARKET' | 'STOP' | 'STOP_MARKET' | 'TAKE_PROFIT' | 'TAKE_PROFIT_MARKET' | 'TRAILING_STOP_MARKET'}
     * @memberof TradeApiNewOrder
     */
    readonly type: NewOrderTypeEnum;

    /**
     *
     * @type {string}
     * @memberof TradeApiNewOrder
     */
    readonly id?: string;

    /**
     * Default `BOTH` for One-way Mode; `LONG` or `SHORT` for Hedge Mode.  It must be sent in Hedge Mode.
     * @type {'BOTH' | 'LONG' | 'SHORT'}
     * @memberof TradeApiNewOrder
     */
    readonly positionSide?: NewOrderPositionSideEnum;

    /**
     *
     * @type {'GTC' | 'IOC' | 'FOK' | 'GTX'}
     * @memberof TradeApiNewOrder
     */
    readonly timeInForce?: NewOrderTimeInForceEnum;

    /**
     * Quantity measured by contract number, Cannot be sent with `closePosition`=`true`
     * @type {number}
     * @memberof TradeApiNewOrder
     */
    readonly quantity?: number;

    /**
     * Cannot be sent in Hedge Mode; cannot be sent with `closePosition`=`true` (Close-All)"
     * @type {'true' | 'false'}
     * @memberof TradeApiNewOrder
     */
    readonly reduceOnly?: NewOrderReduceOnlyEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiNewOrder
     */
    readonly price?: number;

    /**
     * A unique id among open orders. Automatically generated if not sent. Can only be string following the rule: `^[\.A-Z\:/a-z0-9_-]{1,36}$`
     * @type {string}
     * @memberof TradeApiNewOrder
     */
    readonly newClientOrderId?: string;

    /**
     * Used with `STOP/STOP_MARKET` or `TAKE_PROFIT/TAKE_PROFIT_MARKET` orders.
     * @type {number}
     * @memberof TradeApiNewOrder
     */
    readonly stopPrice?: number;

    /**
     * `true`, `false`；Close-All，used with `STOP_MARKET` or `TAKE_PROFIT_MARKET`.
     * @type {'true' | 'false'}
     * @memberof TradeApiNewOrder
     */
    readonly closePosition?: NewOrderClosePositionEnum;

    /**
     * Used with `TRAILING_STOP_MARKET` orders, default as the latest price(supporting different workingType)
     * @type {number}
     * @memberof TradeApiNewOrder
     */
    readonly activationPrice?: number;

    /**
     * Used with `TRAILING_STOP_MARKET` orders, min 0.1, max 10 where 1 for 1%
     * @type {number}
     * @memberof TradeApiNewOrder
     */
    readonly callbackRate?: number;

    /**
     * stopPrice triggered by: "MARK_PRICE", "CONTRACT_PRICE". Default "CONTRACT_PRICE"
     * @type {'MARK_PRICE' | 'CONTRACT_PRICE'}
     * @memberof TradeApiNewOrder
     */
    readonly workingType?: NewOrderWorkingTypeEnum;

    /**
     * Used with `STOP/STOP_MARKET` or `TAKE_PROFIT/TAKE_PROFIT_MARKET` orders.'
     * @type {'true' | 'false'}
     * @memberof TradeApiNewOrder
     */
    readonly priceProtect?: NewOrderPriceProtectEnum;

    /**
     *
     * @type {'ACK' | 'RESULT'}
     * @memberof TradeApiNewOrder
     */
    readonly newOrderRespType?: NewOrderNewOrderRespTypeEnum;

    /**
     * only available for `LIMIT`/`STOP`/`TAKE_PROFIT` order; Can't be passed together with `price`
     * @type {'OPPONENT' | 'OPPONENT_5' | 'OPPONENT_10' | 'OPPONENT_20' | 'QUEUE' | 'QUEUE_5' | 'QUEUE_10' | 'QUEUE_20'}
     * @memberof TradeApiNewOrder
     */
    readonly priceMatch?: NewOrderPriceMatchEnum;

    /**
     * `NONE`: No STP / `EXPIRE_TAKER`:expire taker order when STP triggers/ `EXPIRE_MAKER`:expire taker order when STP triggers/ `EXPIRE_BOTH`:expire both orders when STP triggers
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_BOTH' | 'EXPIRE_MAKER'}
     * @memberof TradeApiNewOrder
     */
    readonly selfTradePreventionMode?: NewOrderSelfTradePreventionModeEnum;

    /**
     *
     * @type {number | bigint}
     * @memberof TradeApiNewOrder
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for positionInformation operation in TradeApi.
 * @interface PositionInformationRequest
 */
export interface PositionInformationRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiPositionInformation
     */
    readonly id?: string;

    /**
     *
     * @type {string}
     * @memberof TradeApiPositionInformation
     */
    readonly marginAsset?: string;

    /**
     *
     * @type {string}
     * @memberof TradeApiPositionInformation
     */
    readonly pair?: string;

    /**
     *
     * @type {number | bigint}
     * @memberof TradeApiPositionInformation
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for queryOrder operation in TradeApi.
 * @interface QueryOrderRequest
 */
export interface QueryOrderRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiQueryOrder
     */
    readonly symbol: string;

    /**
     *
     * @type {string}
     * @memberof TradeApiQueryOrder
     */
    readonly id?: string;

    /**
     *
     * @type {number | bigint}
     * @memberof TradeApiQueryOrder
     */
    readonly orderId?: number | bigint;

    /**
     *
     * @type {string}
     * @memberof TradeApiQueryOrder
     */
    readonly origClientOrderId?: string;

    /**
     *
     * @type {number | bigint}
     * @memberof TradeApiQueryOrder
     */
    readonly recvWindow?: number | bigint;
}

/**
 * TradeApi - object-oriented interface
 * @class TradeApi
 * @extends {WebsocketAPIBase}
 */
export class TradeApi implements TradeApiInterface {
    private readonly websocketBase: WebsocketAPIBase;

    constructor(websocketBase: WebsocketAPIBase) {
        this.websocketBase = websocketBase;
    }

    /**
     * Cancel an active order.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     * - Either `orderId` or `origClientOrderId` must be sent.
     *
     * @summary Cancel Order (TRADE)
     * @param {CancelOrderRequest} requestParameters Request parameters.
     * @returns {Promise<CancelOrderResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-api/trade#cancel-order Binance API Documentation}
     */
    public cancelOrder(
        requestParameters: CancelOrderRequest
    ): Promise<WebsocketApiResponse<CancelOrderResponse>> {
        return this.websocketBase.sendMessage<CancelOrderResponse>(
            '/order.cancel'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Order modify function, currently only LIMIT order modification is
     * supported, modified orders will be reordered in the match queue
     *
     * Weight: 1 on 10s order rate limit(X-MBX-ORDER-COUNT-10S);
     * 1 on 1min order rate limit(X-MBX-ORDER-COUNT-1M);
     * 1 on IP rate limit(x-mbx-used-weight-1m)
     *
     * Security Type: TRADE
     *
     * Notes:
     * - Either `orderId` or `origClientOrderId` must be sent, and the `orderId` will prevail if both are sent.
     * - Both `quantity` and `price` must be sent.
     * - When the new `quantity` or `price` doesn't satisfy `PRICE_FILTER` / `PERCENT_FILTER` / `LOT_SIZE`, amendment will be rejected and the order will stay as it is.
     * - However the order will be cancelled by the amendment in the following situations:
     * - when the order is in partially filled status and the new `quantity` <= `executedQty`
     * - When the order is `GTX` and the new price will cause it to be executed immediately
     * - One order can only be modified for less than 10000 times.
     *
     * @summary Modify Order (TRADE)
     * @param {ModifyOrderRequest} requestParameters Request parameters.
     * @returns {Promise<ModifyOrderResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-api/trade#modify-order Binance API Documentation}
     */
    public modifyOrder(
        requestParameters: ModifyOrderRequest
    ): Promise<WebsocketApiResponse<ModifyOrderResponse>> {
        return this.websocketBase.sendMessage<ModifyOrderResponse>(
            '/order.modify'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Send in a new order.
     *
     * Weight(IP): 0
     *
     * Security Type: TRADE
     *
     * Notes:
     * - Additional mandatory parameters based on `type`:
     * | Type | Additional mandatory parameters |  | :---: | --- |  | `LIMIT` | `timeInForce`, `quantity`, `price` | | `MARKET` | `quantity` | | `STOP/TAKE_PROFIT` | `quantity`, `price`, `stopPrice` | | `STOP_MARKET/TAKE_PROFIT_MARKET` | `stopPrice` | | `TRAILING_STOP_MARKET` | `callbackRate` |
     * - Order with type `STOP`, parameter `timeInForce` can be sent ( default `GTC`). * Order with type `TAKE_PROFIT`, parameter `timeInForce` can be sent ( default `GTC`). * Condition orders will be triggered when:
     * - If parameter `priceProtect` is sent as true:
     * - when price reaches the `stopPrice`，the difference rate between "MARK_PRICE" and "CONTRACT_PRICE" cannot be larger than the "triggerProtect" of the symbol
     * - "triggerProtect" of a symbol can be got from `GET /dapi/v1/exchangeInfo`
     * - `STOP`, `STOP_MARKET`:
     * - BUY: latest price ("MARK_PRICE" or "CONTRACT_PRICE") >= `stopPrice`
     * - SELL: latest price ("MARK_PRICE" or "CONTRACT_PRICE") <= `stopPrice`
     * - `TAKE_PROFIT`, `TAKE_PROFIT_MARKET`:
     * - BUY: latest price ("MARK_PRICE" or "CONTRACT_PRICE") <= `stopPrice`
     * - SELL: latest price ("MARK_PRICE" or "CONTRACT_PRICE") >= `stopPrice`
     * - `TRAILING_STOP_MARKET`:
     * - BUY: the lowest price after order placed <= `activationPrice`, and the latest price >= the lowest price * (1 + `callbackRate`)
     * - SELL: the highest price after order placed >= `activationPrice`, and the latest price <= the highest price * (1 - `callbackRate`)
     * - For `TRAILING_STOP_MARKET`, if you got such error code.
     * - BUY: `activationPrice` should be smaller than latest price.
     * - SELL: `activationPrice` should be larger than latest price.
     * - If `newOrderRespType` is sent as `RESULT`:
     * - `MARKET` order: the final FILLED result of the order will be return directly.
     * - `LIMIT` order with special `timeInForce`: the final status result of the order(FILLED or EXPIRED) will be returned directly.
     * - `STOP_MARKET`, `TAKE_PROFIT_MARKET` with `closePosition=true`:
     * - Follow the same rules for condition orders.
     * - If triggered，**close all** current long position(if `SELL`) or current short position(if `BUY`).
     * - Cannot be used with `quantity` parameter
     * - Cannot be used with `reduceOnly` parameter
     * - In Hedge Mode, cannot be used with `BUY` orders in `LONG` position side. and cannot be used with `SELL` orders in `SHORT` position side
     *
     * @summary New Order (TRADE)
     * @param {NewOrderRequest} requestParameters Request parameters.
     * @returns {Promise<NewOrderResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-api/trade#new-order Binance API Documentation}
     */
    public newOrder(
        requestParameters: NewOrderRequest
    ): Promise<WebsocketApiResponse<NewOrderResponse>> {
        return this.websocketBase.sendMessage<NewOrderResponse>(
            '/order.place'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Get current position information.
     *
     * Weight(IP): 5
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Please use with user data stream `ACCOUNT_UPDATE` to meet your timeliness and accuracy needs.
     *
     * @summary Position Information (USER_DATA)
     * @param {PositionInformationRequest} requestParameters Request parameters.
     * @returns {Promise<PositionInformationResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-api/trade#position-information Binance API Documentation}
     */
    public positionInformation(
        requestParameters: PositionInformationRequest = {}
    ): Promise<WebsocketApiResponse<PositionInformationResponse>> {
        return this.websocketBase.sendMessage<PositionInformationResponse>(
            '/account.position'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Check an order's status.
     *
     * These orders will not be found:
     * order status is `CANCELED` or `EXPIRED` **AND** order has NO filled trade **AND** created time + 3 days < current time
     * order create time + 90 days < current time
     *
     * Weight(IP): 1
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Either `orderId` or `origClientOrderId` must be sent.
     *
     * @summary Query Order (USER_DATA)
     * @param {QueryOrderRequest} requestParameters Request parameters.
     * @returns {Promise<QueryOrderResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/ws-api/trade#query-order Binance API Documentation}
     */
    public queryOrder(
        requestParameters: QueryOrderRequest
    ): Promise<WebsocketApiResponse<QueryOrderResponse>> {
        return this.websocketBase.sendMessage<QueryOrderResponse>(
            '/order.status'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }
}

export enum ModifyOrderSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum ModifyOrderPriceMatchEnum {
    OPPONENT = 'OPPONENT',
    OPPONENT_5 = 'OPPONENT_5',
    OPPONENT_10 = 'OPPONENT_10',
    OPPONENT_20 = 'OPPONENT_20',
    QUEUE = 'QUEUE',
    QUEUE_5 = 'QUEUE_5',
    QUEUE_10 = 'QUEUE_10',
    QUEUE_20 = 'QUEUE_20',
}

export enum NewOrderSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum NewOrderTypeEnum {
    LIMIT = 'LIMIT',
    MARKET = 'MARKET',
    STOP = 'STOP',
    STOP_MARKET = 'STOP_MARKET',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_MARKET = 'TAKE_PROFIT_MARKET',
    TRAILING_STOP_MARKET = 'TRAILING_STOP_MARKET',
}

export enum NewOrderPositionSideEnum {
    BOTH = 'BOTH',
    LONG = 'LONG',
    SHORT = 'SHORT',
}

export enum NewOrderTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
    GTX = 'GTX',
}

export enum NewOrderReduceOnlyEnum {
    TRUE = 'true',
    FALSE = 'false',
}

export enum NewOrderClosePositionEnum {
    TRUE = 'true',
    FALSE = 'false',
}

export enum NewOrderWorkingTypeEnum {
    MARK_PRICE = 'MARK_PRICE',
    CONTRACT_PRICE = 'CONTRACT_PRICE',
}

export enum NewOrderPriceProtectEnum {
    TRUE = 'true',
    FALSE = 'false',
}

export enum NewOrderNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
}

export enum NewOrderPriceMatchEnum {
    OPPONENT = 'OPPONENT',
    OPPONENT_5 = 'OPPONENT_5',
    OPPONENT_10 = 'OPPONENT_10',
    OPPONENT_20 = 'OPPONENT_20',
    QUEUE = 'QUEUE',
    QUEUE_5 = 'QUEUE_5',
    QUEUE_10 = 'QUEUE_10',
    QUEUE_20 = 'QUEUE_20',
}

export enum NewOrderSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
}
